
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Spinner,
  Alert,
  Button,
  Card,
  Modal,
} from "react-bootstrap";

/* ============================================================
   API CONFIGURATION
   ============================================================

   Development:
   https://apimyshop.myidealtd.co.uk/api

   Production:
   https://apimyshop.myidealtd.co.uk/api

   IMPORTANT:
   API_URL = API endpoints
   IMAGE_BASE_URL = image/static-file host
   ============================================================ */

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://apimyshop.myidealtd.co.uk/api"
)
  .trim()
  .replace(/\/+$/, "");

const IMAGE_BASE_URL = API_URL
  .replace(/\/api\/?$/i, "")
  .replace(/\/+$/, "");

const DEFAULT_IMAGE = "/images/no-image.png";

/* ============================================================
   IMAGE URL HELPER
   ============================================================

   Supports:

   1. Full URL
      https://example.com/image.jpg

   2. Image object
      {
        main: "...",
        original: "...",
        thumbnail: "...",
        cart: "..."
      }

   3. /uploads/products/test.jpg

   4. uploads/products/test.jpg

   5. products/test.jpg

   6. images/test.jpg

   7. test.jpg

   8. /api/uploads/products/test.jpg

   9. api/uploads/products/test.jpg
   ============================================================ */

const getImageUrl = (img) => {
  /* ----------------------------------------------------------
     No image
     ---------------------------------------------------------- */

  if (!img) {
    return DEFAULT_IMAGE;
  }

  /* ----------------------------------------------------------
     IMAGE OBJECT

     Prefer main image first.
     ---------------------------------------------------------- */

  if (typeof img === "object") {
    img =
      img.main ||
      img.original ||
      img.thumbnail ||
      img.cart ||
      img.url ||
      img.path ||
      img.filename ||
      "";
  }

  /* ----------------------------------------------------------
     Still no image
     ---------------------------------------------------------- */

  if (!img) {
    return DEFAULT_IMAGE;
  }

  /* ----------------------------------------------------------
     Convert to string
     ---------------------------------------------------------- */

  img = String(img).trim();

  if (!img) {
    return DEFAULT_IMAGE;
  }

  /* ----------------------------------------------------------
     Full HTTP / HTTPS URL

     Example:
     https://apimyshop.myidealtd.co.uk/uploads/a.jpg
     ---------------------------------------------------------- */

  if (
    img.startsWith("http://") ||
    img.startsWith("https://")
  ) {
    return img;
  }

  /* ----------------------------------------------------------
     Remove leading slash

     /uploads/a.jpg
     becomes:
     uploads/a.jpg
     ---------------------------------------------------------- */

  img = img.replace(/^\/+/, "");

  /* ----------------------------------------------------------
     Remove accidental API prefix

     api/uploads/a.jpg
     becomes:
     uploads/a.jpg
     ---------------------------------------------------------- */

  img = img.replace(/^api\/+/i, "");

  /* ----------------------------------------------------------
     Remove accidental /api/ prefix

     /api/uploads/a.jpg
     becomes:
     uploads/a.jpg
     ---------------------------------------------------------- */

  img = img.replace(/^\/?api\/+/i, "");

  /* ----------------------------------------------------------
     uploads/...

     https://apimyshop.myidealtd.co.uk/uploads/...
     ---------------------------------------------------------- */

  if (
    img.toLowerCase().startsWith("uploads/")
  ) {
    return `${IMAGE_BASE_URL}/${img}`;
  }

  /* ----------------------------------------------------------
     products/...

     https://apimyshop.myidealtd.co.uk/uploads/products/...
     ---------------------------------------------------------- */

  if (
    img.toLowerCase().startsWith("products/")
  ) {
    return `${IMAGE_BASE_URL}/uploads/${img}`;
  }

  /* ----------------------------------------------------------
     images/...

     https://apimyshop.myidealtd.co.uk/images/...
     ---------------------------------------------------------- */

  if (
    img.toLowerCase().startsWith("images/")
  ) {
    return `${IMAGE_BASE_URL}/${img}`;
  }

  /* ----------------------------------------------------------
     Plain filename

     product.jpg

     becomes:

     https://apimyshop.myidealtd.co.uk/uploads/product.jpg
     ---------------------------------------------------------- */

  return `${IMAGE_BASE_URL}/uploads/${img}`;
};

/* ============================================================
   PRODUCT LIST
   ============================================================ */

function ProductList() {
  /* ==========================================================
     STATE
     ========================================================== */

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);

  const [selectedProductId, setSelectedProductId] =
    useState(null);

  const [selectedProduct, setSelectedProduct] =
    useState(null);

  const [deleteLoading, setDeleteLoading] =
    useState(false);

  /* ==========================================================
     DEBUG CONFIGURATION
     ========================================================== */

  useEffect(() => {
    console.log(
      "=========================================="
    );

    console.log(
      "ProductList API CONFIG"
    );

    console.log(
      "API_URL:",
      API_URL
    );

    console.log(
      "IMAGE_BASE_URL:",
      IMAGE_BASE_URL
    );

    console.log(
      "=========================================="
    );
  }, []);

  /* ==========================================================
     LOAD PRODUCTS
     ========================================================== */

  const getProducts = async () => {
    try {
      setLoading(true);
      setError("");

      const endpoint =
        `${API_URL}/products`;

      console.log(
        "ProductList REQUEST:",
        endpoint
      );

      const res =
        await axios.get(
          endpoint,
          {
            withCredentials: true,

            headers: {
              "Content-Type":
                "application/json",
            },
          }
        );

      console.log(
        "ProductList RESPONSE:",
        res.data
      );

      /* ------------------------------------------------------
         Backend response formats supported:

         1.
         {
           products: [...]
         }

         2.
         {
           Products: [...]
         }

         3.
         {
           data: {
             products: [...]
           }
         }

         4.
         [...]
         ------------------------------------------------------ */

      let productsArray = [];

      if (
        Array.isArray(res.data)
      ) {
        productsArray =
          res.data;
      }

      else if (
        Array.isArray(
          res.data?.products
        )
      ) {
        productsArray =
          res.data.products;
      }

      else if (
        Array.isArray(
          res.data?.Products
        )
      ) {
        productsArray =
          res.data.Products;
      }

      else if (
        Array.isArray(
          res.data?.data?.products
        )
      ) {
        productsArray =
          res.data.data.products;
      }

      setData(
        productsArray
      );

      /* ------------------------------------------------------
         IMAGE DEBUGGING

         This lets you see exactly what URL React generates.
         ------------------------------------------------------ */

      console.log(
        "=========================================="
      );

      console.log(
        "PRODUCT IMAGE DATA"
      );

      productsArray.forEach(
        (product) => {
          console.log(
            "Product:",
            product?.name
          );

          console.log(
            "Images:",
            product?.images
          );

          console.log(
            "First image:",
            product?.images?.[0]
          );

          console.log(
            "Resolved image URL:",
            getImageUrl(
              product?.images?.[0]
            )
          );
        }
      );

      console.log(
        "=========================================="
      );
    }

    catch (err) {
      console.error(
        "ProductList fetch error:",
        err
      );

      console.error(
        "Status:",
        err.response?.status
      );

      console.error(
        "Response:",
        err.response?.data
      );

      setError(
        err.response?.data?.message ||
        "Error fetching products. Please try again."
      );
    }

    finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     LOAD ON MOUNT
     ========================================================== */

  useEffect(() => {
    getProducts();
  }, []);

  /* ==========================================================
     CONFIRM DELETE
     ========================================================== */

  const confirmDelete = (
    product
  ) => {
    setSelectedProductId(
      product?._id
    );

    setSelectedProduct(
      product
    );

    setShowModal(true);
  };

  /* ==========================================================
     CANCEL DELETE
     ========================================================== */

  const handleCloseModal = () => {
    if (deleteLoading) {
      return;
    }

    setShowModal(false);

    setSelectedProductId(
      null
    );

    setSelectedProduct(
      null
    );
  };

  /* ==========================================================
     DELETE PRODUCT
     ========================================================== */

  const handleDeleteConfirmed =
    async () => {
      if (
        !selectedProductId ||
        deleteLoading
      ) {
        return;
      }

      try {
        setDeleteLoading(
          true
        );

        const endpoint =
          `${API_URL}/products/delete/${selectedProductId}`;

        console.log(
          "DELETE PRODUCT:",
          endpoint
        );

        const res =
          await axios.delete(
            endpoint,
            {
              withCredentials: true,

              headers: {
                "Content-Type":
                  "application/json",
              },
            }
          );

        console.log(
          "DELETE RESPONSE:",
          res.data
        );

        if (
          res.status >= 200 &&
          res.status < 300
        ) {
          /* --------------------------------------------
             Remove product immediately from UI
             -------------------------------------------- */

          setData(
            (prev) =>
              prev.filter(
                (item) =>
                  String(
                    item?._id
                  ) !==
                  String(
                    selectedProductId
                  )
              )
          );

          setShowModal(
            false
          );

          setSelectedProductId(
            null
          );

          setSelectedProduct(
            null
          );
        }
      }

      catch (err) {
        console.error(
          "Delete failed:",
          err
        );

        console.error(
          "Status:",
          err.response?.status
        );

        console.error(
          "Data:",
          err.response?.data
        );

        alert(
          err.response?.data?.message ||
          "Failed to delete product."
        );
      }

      finally {
        setDeleteLoading(
          false
        );
      }
    };

  /* ==========================================================
     IMAGE ERROR HANDLER
     ========================================================== */

  const handleImageError = (
    e,
    product
  ) => {
    const failedUrl =
      e.currentTarget.src;

    console.error(
      "=========================================="
    );

    console.error(
      "PRODUCT IMAGE FAILED"
    );

    console.error(
      "Product:",
      product?.name
    );

    console.error(
      "Image data:",
      product?.images
    );

    console.error(
      "Failed URL:",
      failedUrl
    );

    console.error(
      "Resolved URL:",
      getImageUrl(
        product?.images?.[0]
      )
    );

    console.error(
      "=========================================="
    );

    /* --------------------------------------------------------
       Prevent infinite fallback loop
       -------------------------------------------------------- */

    if (
      failedUrl.includes(
        DEFAULT_IMAGE
      )
    ) {
      return;
    }

    e.currentTarget.onerror =
      null;

    e.currentTarget.src =
      DEFAULT_IMAGE;
  };

  /* ==========================================================
     LOADING STATE
     ========================================================== */

  if (loading) {
    return (
      <div
        style={{
          minHeight:
            "400px",

          display:
            "flex",

          alignItems:
            "center",

          justifyContent:
            "center",

          padding:
            "3rem",
        }}
      >
        <Spinner
          animation="border"
          variant="primary"
        />
      </div>
    );
  }

  /* ==========================================================
     PAGE
     ========================================================== */

  return (
    <div
      style={{
        padding:
          "1.5rem",

        background:
          "linear-gradient(180deg,#f8fafc 0%,#ffffff 100%)",

        minHeight:
          "100vh",
      }}
    >

      {/* ======================================================
          HEADER
          ====================================================== */}

      <div
        style={{
          display:
            "flex",

          justifyContent:
            "space-between",

          alignItems:
            "center",

          flexWrap:
            "wrap",

          gap:
            "15px",

          marginBottom:
            "2rem",
        }}
      >

        <div>
          <h3
            style={{
              margin:
                0,

              fontWeight:
                800,

              color:
                "#111827",

              letterSpacing:
                "-0.5px",
            }}
          >
            Products
          </h3>

          <div
            style={{
              marginTop:
                "5px",

              color:
                "#64748b",

              fontSize:
                "14px",
            }}
          >
            {data.length}{" "}
            {data.length === 1
              ? "product"
              : "products"}
          </div>
        </div>

        <Link
          to="/AddProduct"
          title="Create Product"
          style={{
            width:
              "48px",

            height:
              "48px",

            background:
              "linear-gradient(135deg,#111827,#374151)",

            color:
              "#fff",

            borderRadius:
              "50%",

            display:
              "flex",

            justifyContent:
              "center",

            alignItems:
              "center",

            textDecoration:
              "none",

            fontSize:
              "25px",

            fontWeight:
              400,

            boxShadow:
              "0 10px 25px rgba(0,0,0,.18)",

            transition:
              "all .25s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform =
              "translateY(-3px) scale(1.05)";

            e.currentTarget.style.boxShadow =
              "0 15px 30px rgba(0,0,0,.25)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform =
              "translateY(0) scale(1)";

            e.currentTarget.style.boxShadow =
              "0 10px 25px rgba(0,0,0,.18)";
          }}
        >
          +
        </Link>
      </div>

      {/* ======================================================
          ERROR
          ====================================================== */}

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() =>
            setError("")
          }
        >
          <strong>
            Unable to load products.
          </strong>

          <div
            style={{
              marginTop:
                "5px",
            }}
          >
            {error}
          </div>
        </Alert>
      )}

      {/* ======================================================
          EMPTY STATE
          ====================================================== */}

      {!error &&
        data.length === 0 && (
          <Alert
            variant="info"
            style={{
              borderRadius:
                "16px",
            }}
          >
            No products found.
          </Alert>
        )}

      {/* ======================================================
          PRODUCT GRID
          ====================================================== */}

      {data.length > 0 && (
        <div
          style={{
            display:
              "grid",

            gridTemplateColumns:
              "repeat(auto-fit,minmax(280px,1fr))",

            gap:
              "24px",

            width:
              "100%",
          }}
        >

          {data.map(
            (item) => {

              const imageUrl =
                getImageUrl(
                  item?.images?.[0]
                );

              const stock =
                typeof item?.stock !==
                "undefined"
                  ? item.stock
                  : item?.countInStock;

              const isOutOfStock =
                Number(stock) <= 0;

              return (
                <Card
                  key={
                    item?._id
                  }

                  style={{
                    height:
                      "100%",

                    border:
                      "1px solid rgba(15,23,42,.06)",

                    borderRadius:
                      "24px",

                    overflow:
                      "hidden",

                    background:
                      "rgba(255,255,255,.92)",

                    boxShadow:
                      "0 12px 35px rgba(15,23,42,.08)",

                    backdropFilter:
                      "blur(12px)",

                    transition:
                      "transform .3s ease, box-shadow .3s ease",
                  }}

                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform =
                      "translateY(-6px)";

                    e.currentTarget.style.boxShadow =
                      "0 20px 45px rgba(15,23,42,.14)";
                  }}

                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform =
                      "translateY(0)";

                    e.currentTarget.style.boxShadow =
                      "0 12px 35px rgba(15,23,42,.08)";
                  }}
                >

                  {/* ==================================================
                      IMAGE CONTAINER
                      ================================================== */}

                  <div
                    style={{
                      position:
                        "relative",

                      width:
                        "100%",

                      height:
                        "240px",

                      background:
                        "#f8fafc",

                      display:
                        "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                      overflow:
                        "hidden",

                      borderBottom:
                        "1px solid rgba(15,23,42,.05)",
                    }}
                  >

                    {/* OUT OF STOCK */}

                    {isOutOfStock && (
                      <div
                        style={{
                          position:
                            "absolute",

                          top:
                            "15px",

                          right:
                            "15px",

                          zIndex:
                            3,

                          padding:
                            "7px 12px",

                          borderRadius:
                            "999px",

                          background:
                            "rgba(220,38,38,.92)",

                          color:
                            "#fff",

                          fontSize:
                            "11px",

                          fontWeight:
                            800,

                          letterSpacing:
                            ".4px",
                        }}
                      >
                        OUT OF STOCK
                      </div>
                    )}

                    {/* PRODUCT IMAGE */}

                    <img
                      src={
                        imageUrl
                      }

                      alt={
                        item?.name ||
                        "Product"
                      }

                      loading="lazy"

                      onError={(
                        e
                      ) =>
                        handleImageError(
                          e,
                          item
                        )
                      }

                      style={{
                        width:
                          "100%",

                        height:
                          "100%",

                        objectFit:
                          "contain",

                        padding:
                          "18px",

                        display:
                          "block",

                        transition:
                          "transform .35s ease",
                      }}

                      onMouseEnter={(
                        e
                      ) => {
                        e.currentTarget.style.transform =
                          "scale(1.04)";
                      }}

                      onMouseLeave={(
                        e
                      ) => {
                        e.currentTarget.style.transform =
                          "scale(1)";
                      }}
                    />

                  </div>

                  {/* ==================================================
                      CARD BODY
                      ================================================== */}

                  <Card.Body
                    style={{
                      display:
                        "flex",

                      flexDirection:
                        "column",

                      padding:
                        "20px",
                    }}
                  >

                    {/* PRODUCT NAME */}

                    <Card.Title
                      style={{
                        color:
                          "#111827",

                        fontWeight:
                          750,

                        fontSize:
                          "18px",

                        marginBottom:
                          "10px",

                        lineHeight:
                          1.3,
                      }}
                    >
                      {item?.name ||
                        "Unnamed Product"}
                    </Card.Title>

                    {/* PRODUCT DETAILS */}

                    <Card.Text
                      style={{
                        fontSize:
                          "13px",

                        color:
                          "#64748b",

                        lineHeight:
                          1.7,

                        marginBottom:
                          "12px",
                      }}
                    >

                      <strong>
                        Brand:
                      </strong>{" "}
                      {typeof item?.brand ===
                      "object"
                        ? item?.brand?.name ||
                          "—"
                        : item?.brand ||
                          "—"}

                      <br />

                      <strong>
                        Category:
                      </strong>{" "}
                      {typeof item?.category ===
                      "object"
                        ? item?.category?.name ||
                          "—"
                        : item?.category ||
                          "—"}

                      <br />

                      <strong>
                        Price:
                      </strong>{" "}
                      £
                      {Number(
                        item?.price ||
                          0
                      ).toFixed(2)}

                      <br />

                      <strong>
                        Stock:
                      </strong>{" "}
                      {typeof stock !==
                      "undefined"
                        ? stock
                        : "—"}
                    </Card.Text>

                    {/* DESCRIPTION */}

                    <Card.Text
                      style={{
                        fontSize:
                          "14px",

                        color:
                          "#475569",

                        lineHeight:
                          1.6,

                        flexGrow:
                          1,

                        display:
                          "-webkit-box",

                        WebkitLineClamp:
                          3,

                        WebkitBoxOrient:
                          "vertical",

                        overflow:
                          "hidden",

                        marginBottom:
                          "20px",
                      }}
                    >
                      {item?.description ||
                        "No description available."}
                    </Card.Text>

                    {/* ACTIONS */}

                    <div
                      style={{
                        display:
                          "flex",

                        gap:
                          "10px",

                        marginTop:
                          "auto",
                      }}
                    >

                      {/* EDIT */}

                      <Link
                        to={`/edit/${item?._id}`}
                        style={{
                          flex:
                            1,

                          textDecoration:
                            "none",
                        }}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{
                            width:
                              "100%",

                            borderRadius:
                              "12px",

                            fontWeight:
                              600,

                            padding:
                              "9px 12px",
                          }}
                        >
                          Edit
                        </Button>
                      </Link>

                      {/* DELETE */}

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          confirmDelete(
                            item
                          )
                        }
                        style={{
                          flex:
                            1,

                          borderRadius:
                            "12px",

                          fontWeight:
                            600,

                          padding:
                            "9px 12px",
                        }}
                      >
                        Delete
                      </Button>

                    </div>

                  </Card.Body>
                </Card>
              );
            }
          )}

        </div>
      )}

      {/* ======================================================
          DELETE MODAL
          ====================================================== */}

      <Modal
        show={
          showModal
        }

        onHide={
          handleCloseModal
        }

        centered

        backdrop="static"

        keyboard={
          !deleteLoading
        }
      >

        <Modal.Header
          closeButton={
            !deleteLoading
          }

          style={{
            border:
              "none",

            padding:
              "24px 24px 10px",
          }}
        >

          <Modal.Title
            style={{
              fontWeight:
                800,

              color:
                "#111827",
            }}
          >
            Confirm Deletion
          </Modal.Title>

        </Modal.Header>

        <Modal.Body
          style={{
            padding:
              "15px 24px 25px",

            color:
              "#475569",
          }}
        >

          <div
            style={{
              fontSize:
                "15px",

              lineHeight:
                1.7,
            }}
          >
            Are you sure you want to delete:

            <div
              style={{
                marginTop:
                  "10px",

                padding:
                  "14px 16px",

                borderRadius:
                  "14px",

                background:
                  "#f8fafc",

                color:
                  "#111827",

                fontWeight:
                  700,

                border:
                  "1px solid #e2e8f0",
              }}
            >
              {selectedProduct?.name ||
                "this product"}
            </div>

            <div
              style={{
                marginTop:
                  "12px",

                fontSize:
                  "13px",

                color:
                  "#64748b",
              }}
            >
              This action cannot be
              undone.
            </div>
          </div>

        </Modal.Body>

        <Modal.Footer
          style={{
            border:
              "none",

            padding:
              "10px 24px 24px",
          }}
        >

          <Button
            variant="light"
            onClick={
              handleCloseModal
            }

            disabled={
              deleteLoading
            }

            style={{
              borderRadius:
                "12px",

              padding:
                "10px 18px",

              fontWeight:
                600,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            onClick={
              handleDeleteConfirmed
            }

            disabled={
              deleteLoading
            }

            style={{
              borderRadius:
                "12px",

              padding:
                "10px 20px",

              fontWeight:
                700,

              minWidth:
                "125px",
            }}
          >

            {deleteLoading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{
                    marginRight:
                      "8px",
                  }}
                />

                Deleting...
              </>
            ) : (
              "Yes, Delete"
            )}

          </Button>

        </Modal.Footer>

      </Modal>

    </div>
  );
}

export default ProductList;

